import type { CodexCacheEfficiency } from '@ace-tracker/shared';

/**
 * Codex's cache-hit visual — modeled on CacheEfficiencyChart's two-bar
 * layout, but NOT the same formula: Codex's cachedInputTokens is INCLUSIVE
 * of inputTokens, so the two bars here are "billable" (fresh) input vs.
 * cached input, not two independently-summed volumes like Claude Code's
 * cache-creation/cache-read. See collector/docs/ARCHITECTURE.md "The Codex
 * collector".
 */
export function CodexCacheEfficiencyChart({ cacheEfficiency }: { cacheEfficiency: CodexCacheEfficiency }) {
  const billableInputTokens = Math.max(0, cacheEfficiency.inputTokens - cacheEfficiency.cachedInputTokens);
  const max = Math.max(billableInputTokens, cacheEfficiency.cachedInputTokens, 1);
  const hasVolume = cacheEfficiency.inputTokens > 0;

  return (
    <div className="flex h-full flex-col justify-center">
      <div className="grid gap-5">
        <Row label="Fresh input (billable, not cached)" value={billableInputTokens} max={max} colorClassName="bg-primary-blue" />
        <Row label="Cached input (context reused)" value={cacheEfficiency.cachedInputTokens} max={max} colorClassName="bg-success" />
      </div>
      {hasVolume && (
        <p className="mt-5 border-t border-border pt-3 text-xs text-text-muted">
          <span className="font-semibold text-text-default">{Math.round(cacheEfficiency.cacheHitRatio * 100)}%</span> of
          input tokens were served from cache rather than sent fresh — a higher share means cheaper, faster turns.
        </p>
      )}
    </div>
  );
}

function Row({ label, value, max, colorClassName }: { label: string; value: number; max: number; colorClassName: string }) {
  const widthPercent = Math.max((value / max) * 100, value > 0 ? 0.6 : 0);
  const fitsInside = widthPercent > 22;
  return (
    <div>
      <p className="mb-1.5 text-[11px] uppercase tracking-wide text-text-faint">{label}</p>
      <div className="relative h-9">
        <div className={`absolute inset-y-0 left-0 rounded ${colorClassName}`} style={{ width: `${widthPercent}%` }} />
        <span
          className={`absolute inset-y-0 flex items-center whitespace-nowrap text-sm font-bold tabular-nums ${
            fitsInside ? 'left-0 px-3 text-text-inverse' : 'text-text-default'
          }`}
          style={fitsInside ? undefined : { left: `calc(${widthPercent}% + 10px)` }}
        >
          {value.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
